import { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Send error to Sentry
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          style={{
            minHeight: '100vh',
            backgroundColor: 'var(--cld-color-off-white)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--cld-spacing-xl)',
            fontFamily: 'var(--cld-font-body)',
          }}
        >
          <div
            style={{
              maxWidth: '480px',
              width: '100%',
              backgroundColor: 'var(--cld-color-white)',
              borderRadius: 'var(--cld-radius-lg)',
              boxShadow: 'var(--cld-shadow-lg)',
              padding: 'var(--cld-spacing-xl)',
              textAlign: 'center',
            }}
          >
            <img
              src="/assets/logo.png"
              alt="ClosedLoopDiving"
              style={{ height: '36px', marginBottom: 'var(--cld-spacing-lg)' }}
            />
            <svg
              style={{ margin: '0 auto var(--cld-spacing-md)', display: 'block', color: 'var(--cld-color-error)' }}
              width="48"
              height="48"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <h1
              style={{
                marginTop: 'var(--cld-spacing-sm)',
                fontSize: 'var(--cld-font-size-xl)',
                fontWeight: 700,
                color: 'var(--cld-color-primary-dark)',
                fontFamily: 'var(--cld-font-display)',
              }}
            >
              Something went wrong
            </h1>
            <p
              style={{
                marginTop: 'var(--cld-spacing-sm)',
                color: 'var(--cld-color-charcoal)',
                lineHeight: 1.6,
              }}
            >
              We're sorry for the inconvenience. The error has been reported and
              we'll look into it.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                marginTop: 'var(--cld-spacing-lg)',
                backgroundColor: 'var(--cld-color-secondary)',
                color: 'var(--cld-color-white)',
                padding: '10px 24px',
                borderRadius: 'var(--cld-radius-md)',
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'var(--cld-font-display)',
                fontSize: 'var(--cld-font-size-base)',
              }}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
